import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  X,
  Search,
  Inbox,
  CheckCircle2,
  AlertCircle,
  Wallet,
  Crown,
  Building2,
  MapPin,
  Mail,
  Phone,
  Send,
  Download,
  Mailbox,
  Link as LinkIcon,
  Copy,
  MessageCircle,
  Trash2,
  Zap,
  Sparkles,
} from "lucide-react";

const TABS = [
  { key: "unrouted", label: "Unrouted", icon: AlertCircle },
  { key: "routed", label: "Routed", icon: CheckCircle2 },
  { key: "all", label: "All", icon: Inbox },
];

const fmt$ = (cents) => "$" + Number((cents || 0) / 100).toFixed(2);
const fmtQuote = (v) => "$" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function AdminLeadHub() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [tab, setTab] = useState("unrouted");
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (user && user.role !== "platform_admin") {
      nav("/dashboard");
    }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { status: tab, days } });
      setLeads(data.leads || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, days]);

  const counts = useMemo(() => {
    // Counts shown per tab are based on current result set for current days window
    return {
      unrouted: leads.filter((l) => !l.assigned_to).length,
      routed: leads.filter((l) => !!l.assigned_to).length,
      total: leads.length,
    };
  }, [leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      return (
        (l.first_name || "").toLowerCase().includes(q) ||
        (l.last_name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.phone || "").toLowerCase().includes(q) ||
        (l.zip_code || "").toLowerCase().includes(q) ||
        (l.destination_company_name || "").toLowerCase().includes(q)
      );
    });
  }, [leads, search]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.info("Nothing to export");
      return;
    }
    const headers = [
      "created_at", "name", "email", "phone", "zip", "state", "quote_total",
      "routing_status", "destination_company", "priority_tier", "charge_cents",
      "session_id", "capture_mode",
    ];
    const rows = filtered.map((l) => [
      l.created_at,
      `${l.first_name || ""} ${l.last_name || ""}`.trim(),
      l.email || "",
      l.phone || "",
      l.zip_code || "",
      l.state || "",
      l.estimate_total || 0,
      l.routing_status || (l.assigned_to ? "assigned" : "pending"),
      l.destination_company_name || "",
      l.priority_tier || "",
      l.charge_cents || 0,
      l.session_id || "",
      l.capture_mode || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-leads-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => nav("/admin")}
              data-testid="admin-leads-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
            </Button>
            <div>
              <div className="font-display font-bold text-slate-900">Admin Lead Hub</div>
              <div className="text-xs text-slate-500">Every homeowner quote · route orphan leads to companies</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-leads-tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-bold transition-colors ${
                    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`admin-leads-range-${d}`}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold ${
                  days === d ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>

          <div className="relative ml-auto w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search name, email, zip…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-xl"
              data-testid="admin-leads-search"
            />
          </div>

          <Button
            variant="outline"
            className="rounded-xl h-10"
            onClick={exportCsv}
            disabled={!filtered.length}
            data-testid="admin-leads-export-btn"
          >
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
        </div>

        {/* Summary stat strip — reflects current view */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="In view" value={leads.length} />
          <Stat label="Unrouted (view)" value={counts.unrouted} accent="text-amber-600" />
          <Stat label="Routed (view)" value={counts.routed} accent="text-emerald-600" />
          <Stat label="Window" value={`${days}d`} accent="text-[#C75D33]" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Received</th>
                  <th className="text-left px-5 py-3 font-bold">Homeowner</th>
                  <th className="text-left px-5 py-3 font-bold">Location</th>
                  <th className="text-right px-5 py-3 font-bold">Quote</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Routed to</th>
                  <th className="text-right px-5 py-3 font-bold w-0"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-slate-500">
                      <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading leads…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-slate-500" data-testid="admin-leads-empty">
                      {search
                        ? "No leads match your search."
                        : tab === "unrouted"
                        ? "🎉 No orphan leads. Every quote in this window found a home."
                        : tab === "routed"
                        ? "No routed leads in this window yet."
                        : "No leads in this window yet."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((l) => (
                    <LeadRow key={l.id} lead={l} onSelect={() => setSelected(l)} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-[11px] text-slate-400 mt-4 leading-relaxed">
          Unrouted = no marketplace company matched (service, coverage, quote minimum, or wallet balance). Use “Route manually” to hand a lead to a specific company — free or with normal wallet charge.
        </div>
      </main>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onRouted={() => { setSelected(null); load(); }}
          onDeleted={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function LeadRow({ lead, onSelect }) {
  const assigned = !!lead.assigned_to;
  const created = new Date(lead.created_at);
  return (
    <tr className="hover:bg-slate-50 cursor-pointer" onClick={onSelect} data-testid={`admin-lead-row-${lead.id}`}>
      <td className="px-5 py-3 text-slate-600">
        <div className="font-semibold text-slate-900">{created.toLocaleDateString()}</div>
        <div className="text-xs text-slate-500">{created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
      </td>
      <td className="px-5 py-3">
        <div className="font-semibold text-slate-900">
          {(lead.first_name || "") + (lead.last_name ? " " + lead.last_name : "")}
        </div>
        <div className="text-xs text-slate-500">{lead.email || "—"}</div>
      </td>
      <td className="px-5 py-3 text-slate-700">
        <div className="font-mono text-xs">{lead.zip_code || "—"}</div>
        <div className="text-xs text-slate-500">{lead.state || ""}</div>
      </td>
      <td className="px-5 py-3 text-right font-bold text-slate-900 tabular-nums">
        {fmtQuote(lead.estimate_total)}
      </td>
      <td className="px-5 py-3">
        <RoutingBadge lead={lead} />
      </td>
      <td className="px-5 py-3">
        {assigned ? (
          <div className="text-xs">
            <div className="font-semibold text-slate-900">{lead.destination_company_name || lead.destination_company_slug || "—"}</div>
            <div className="text-slate-500">
              {lead.priority_tier ? <span className="capitalize">{lead.priority_tier}</span> : ""}
              {lead.charge_cents != null && lead.charge_cents > 0 ? <> · {fmt$(lead.charge_cents)}</> : null}
              {lead.charge_cents === 0 ? <> · free</> : null}
            </div>
          </div>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="px-5 py-3 text-right">
        <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`admin-lead-open-${lead.id}`}>
          Open
        </Button>
      </td>
    </tr>
  );
}

function RoutingBadge({ lead }) {
  const status = lead.routing_status || (lead.assigned_to ? "assigned" : "pending");
  if (status === "assigned") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
        <CheckCircle2 className="w-3 h-3" /> Assigned
      </span>
    );
  }
  if (status === "no_eligible_company") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
        <AlertCircle className="w-3 h-3" /> No match
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
      Pending
    </span>
  );
}

function LeadDrawer({ lead, onClose, onRouted, onDeleted }) {
  const [detail, setDetail] = useState(lead);
  const [companies, setCompanies] = useState([]);
  const [loadingCo, setLoadingCo] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: d }, { data: cs }] = await Promise.all([
          api.get(`/admin/leads/${lead.id}`),
          api.get("/admin/companies/lookup"),
        ]);
        if (!cancelled) {
          setDetail({ ...lead, ...d });
          setCompanies(cs.companies || []);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load lead detail");
      } finally {
        if (!cancelled) setLoadingCo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const assigned = !!detail.assigned_to;
  const services = detail.services || [];

  const handleDelete = async () => {
    const msg = assigned
      ? `Delete this lead system-wide? It was already routed to a company — their lead card will disappear, but the marketplace assignment + wallet charge records stay in the ledger for accounting.`
      : `Delete this lead system-wide?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/leads/${detail.id}`);
      toast.success("Lead deleted");
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-lead-drawer-backdrop"
    >
      <div
        className="bg-white rounded-3xl max-w-3xl w-full my-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-lead-drawer"
      >
        <div className="px-6 sm:px-8 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#C75D33]">Lead</div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-0.5">
              {(detail.first_name || "") + (detail.last_name ? " " + detail.last_name : "")}
            </h2>
            <div className="text-xs text-slate-500 mt-1 font-mono">id: {detail.id}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition"
              title="Delete lead (admin)"
              data-testid="admin-lead-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              data-testid="admin-lead-drawer-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-6">
          {/* Contact + summary */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field icon={Mail} label="Email" value={detail.email || "—"} />
            <Field icon={Phone} label="Phone" value={detail.phone || "—"} />
            <Field icon={MapPin} label="ZIP" value={`${detail.zip_code || "—"}${detail.state ? " · " + detail.state : ""}`} />
            <Field icon={Wallet} label="Quote total" value={fmtQuote(detail.estimate_total)} />
          </div>

          {/* Services */}
          {services.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Services requested</div>
              <div className="flex flex-wrap gap-1.5">
                {services.map((s, i) => (
                  <span key={i} className="text-xs font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-800 capitalize">
                    {(s.service_key || "").replace(/_/g, " ")} × {s.quantity || 1}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Routing decision */}
          <RoutingDecision detail={detail} />

          {/* Manual route panel */}
          {!assigned && (
            <ManualRoute
              lead={detail}
              companies={companies}
              loading={loadingCo}
              onRouted={onRouted}
            />
          )}

          {/* Invite companies — convert orphan lead into a new paying customer */}
          {!assigned && (
            <InviteCompanies lead={detail} />
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-semibold text-slate-900 mt-0.5 break-words">{value}</div>
    </div>
  );
}

function RoutingDecision({ detail }) {
  const a = detail.assignment;
  const status = detail.routing_status || (detail.assigned_to ? "assigned" : "pending");
  if (status === "assigned" && a) {
    const dest = detail.destination_company || {};
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Routing decision</div>
        </div>
        <div className="text-sm text-slate-800">
          Routed to <strong>{dest.business_name || a.company_slug}</strong>
          {a.manual_route ? <> · <span className="italic">manual hand-off ({a.manual_mode})</span></> : <> · auto-matched</>}
        </div>
        <div className="text-xs text-slate-600 mt-1">
          Tier: <span className="font-bold capitalize">{a.priority_tier}</span> ·
          Charge: <span className="font-bold">{fmt$(a.charge_cents)}</span> ·
          {a.created_at && <> {new Date(a.created_at).toLocaleString()}</>}
        </div>
      </div>
    );
  }
  if (status === "no_eligible_company") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="w-4 h-4 text-amber-700" />
          <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Routing decision</div>
        </div>
        <div className="text-sm text-slate-800">
          No marketplace company matched. Common reasons: nobody covers this ZIP, no priority-tier company offers these services, quote was below their minimum, or wallets were empty.
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
      Routing pending.
    </div>
  );
}

function ManualRoute({ lead, companies, loading, onRouted }) {
  const [companyId, setCompanyId] = useState("");
  const [mode, setMode] = useState("free");
  const [busy, setBusy] = useState(false);

  const eligible = useMemo(() => {
    return companies.filter((c) => c.id); // platform admin can route to any active company
  }, [companies]);

  const selected = eligible.find((c) => c.id === companyId);

  const submit = async () => {
    if (!companyId) {
      toast.error("Pick a company first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/route`, { company_id: companyId, mode });
      const charge = data.charge_cents || 0;
      toast.success(charge > 0 ? `Routed · ${fmt$(charge)} charged` : "Routed (free hand-off)");
      onRouted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't route");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <Send className="w-4 h-4 text-[#C75D33]" />
        <div className="text-sm font-bold text-slate-900">Route this lead manually</div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Hand off to any active company. Use <strong>Free</strong> to convert non-marketplace pros into customers, or <strong>Charge wallet</strong> to bill them at their normal priority-tier rate.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Company</div>
          <Select value={companyId} onValueChange={setCompanyId} disabled={loading}>
            <SelectTrigger className="h-10 rounded-xl" data-testid="admin-route-company-select">
              <SelectValue placeholder={loading ? "Loading…" : "Pick a company"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {eligible.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`admin-route-company-opt-${c.id}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-semibold">{c.business_name}</span>
                    {c.marketplace_enabled ? (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#C75D33] ml-1">
                        <Crown className="w-3 h-3" /> {c.priority_tier}
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400 ml-1">no marketplace</span>
                    )}
                  </span>
                </SelectItem>
              ))}
              {!loading && eligible.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500">No active companies yet</div>
              )}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Billing</div>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="h-10 rounded-xl" data-testid="admin-route-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free" data-testid="admin-route-mode-free">Free hand-off · $0</SelectItem>
              <SelectItem value="charge" data-testid="admin-route-mode-charge">Charge wallet · normal tier rate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {selected && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 mb-3" data-testid="admin-route-preview">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><strong className="text-slate-900">Wallet:</strong> {fmt$(selected.wallet_balance_cents)}</span>
            <span><strong className="text-slate-900">Tier:</strong> <span className="capitalize">{selected.priority_tier}</span></span>
            <span><strong className="text-slate-900">Marketplace:</strong> {selected.marketplace_enabled ? "enabled" : "disabled"}</span>
          </div>
        </div>
      )}

      <Button
        onClick={submit}
        disabled={busy || !companyId}
        className="w-full sm:w-auto rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-11 px-5"
        data-testid="admin-route-submit-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "charge" ? "Route & charge wallet" : "Route for free")}
      </Button>
    </div>
  );
}


function InviteCompanies({ lead }) {
  const [input, setInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/invites`);
      setHistory(data.invites || []);
    } catch {
      // silent — history is optional
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lead.id]);

  const addEmail = (raw) => {
    const candidate = (raw || "").trim().toLowerCase().replace(/[,;]+$/, "");
    if (!candidate) return;
    if (!candidate.includes("@") || !candidate.split("@")[1].includes(".")) {
      toast.error(`"${candidate}" doesn't look like a valid email`);
      return;
    }
    if (emails.includes(candidate)) return;
    setEmails([...emails, candidate]);
    setInput("");
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmail(input);
    } else if (e.key === "Backspace" && !input && emails.length) {
      setEmails(emails.slice(0, -1));
    }
  };

  const remove = (addr) => setEmails(emails.filter((e) => e !== addr));

  const submit = async () => {
    const final = [...emails];
    if (input.trim()) final.push(input.trim().toLowerCase());
    const unique = Array.from(new Set(final.filter(Boolean)));
    if (!unique.length) {
      toast.error("Add at least one email first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/invite`, { emails: unique });
      if (data.email_provider_configured) {
        toast.success(`Invites sent to ${data.delivered}/${data.total}`);
      } else {
        toast.info(`Invites recorded (${data.total}). Email delivery is offline — share the claim links manually.`);
      }
      setEmails([]);
      setInput("");
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send invites");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white" data-testid="admin-invite-panel">
      <div className="flex items-center gap-2 mb-3">
        <Mailbox className="w-4 h-4 text-[#C75D33]" />
        <div className="text-sm font-bold text-slate-900">Invite local companies to claim this lead</div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Email screen pros in this area a 1-click claim link. They start a $1 trial and this lead is free-routed to their dashboard the moment they sign up — a B2B acquisition channel paid for by orphan leads themselves.
      </p>

      <ShareableLinkGenerator lead={lead} onGenerated={loadHistory} />

      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 mt-5">Or email one or more companies</div>

      <div className="rounded-xl border border-slate-200 px-3 py-2 mb-3 min-h-[44px] flex flex-wrap items-center gap-1.5 bg-white" data-testid="admin-invite-input-wrap">
        {emails.map((addr) => (
          <span key={addr} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-800" data-testid={`admin-invite-chip-${addr}`}>
            {addr}
            <button onClick={() => remove(addr)} className="text-slate-400 hover:text-rose-600" type="button" aria-label={`Remove ${addr}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => input.trim() && addEmail(input)}
          placeholder={emails.length ? "" : "owner@acmescreens.com, jane@somerepair.com…"}
          className="flex-1 min-w-[200px] text-sm outline-none bg-transparent py-1"
          data-testid="admin-invite-email-input"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={submit}
          disabled={busy || (emails.length === 0 && !input.trim())}
          className="rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-11 px-5"
          data-testid="admin-invite-submit-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send {emails.length || (input.trim() ? 1 : "")} invite{(emails.length + (input.trim() ? 1 : 0)) === 1 ? "" : "s"}</>}
        </Button>
        <AutoInviteButton lead={lead} onDone={loadHistory} />
        <div className="text-[11px] text-slate-500">
          Press <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-[10px] font-mono">Enter</kbd> or <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-[10px] font-mono">,</kbd> to add another email.
        </div>
      </div>

      {/* History */}
      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Invite history</div>
        {loadingHistory ? (
          <div className="text-xs text-slate-400">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="admin-invite-history-empty">No invites sent yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden" data-testid="admin-invite-history">
            {history.slice(0, 8).map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                <span className="font-mono text-slate-700 truncate">{inv.email}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-slate-400">{new Date(inv.sent_at).toLocaleString()}</span>
                  <InviteStatusBadge status={inv.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InviteStatusBadge({ status }) {
  const map = {
    claimed: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Claimed" },
    sent: { bg: "bg-slate-100", text: "text-slate-700", label: "Sent" },
    expired: { bg: "bg-amber-100", text: "text-amber-800", label: "Expired" },
  };
  const m = map[status] || map.sent;
  return (
    <span className={`inline-flex items-center text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
}

/**
 * ShareableLinkGenerator — one-click "Generate claim link + SMS text"
 *
 * Solves: some screen companies don't publish email addresses, and Twilio
 * hasn't approved the platform's TFN yet. The platform admin can:
 *   1. Click "Generate shareable link"
 *   2. Copy the pre-drafted SMS or just the URL
 *   3. Paste it into iMessage / any texting app on their personal phone
 *   4. The recipient clicks → signs up → auto-claims the lead
 *
 * Uses the same /invites/{token} public preview page as emailed invites so
 * no additional recipient-side UI is needed.
 */
function ShareableLinkGenerator({ lead, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);   // { claim_url, sms_text }
  const [copiedField, setCopiedField] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/invite-link`);
      setLink({ claim_url: data.claim_url, sms_text: data.sms_text });
      onGenerated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(field === "sms" ? "Message copied — paste it into your texting app" : "Link copied");
      setTimeout(() => setCopiedField(null), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the field to select instead");
    }
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 mb-4" data-testid="shareable-link-block">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle className="w-4 h-4 text-emerald-700" />
        <div className="text-sm font-bold text-slate-900">Text it from your phone</div>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed mb-3">
        Generate a shareable claim link + a pre-drafted SMS you can text from your own phone (great when a company doesn&apos;t list an email).
      </p>

      {!link ? (
        <Button
          onClick={generate}
          disabled={busy}
          data-testid="generate-share-link-btn"
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-4 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LinkIcon className="w-4 h-4" /> Generate shareable link</>}
        </Button>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Pre-drafted SMS (recommended)</div>
            <div className="flex items-start gap-2">
              <textarea
                readOnly
                value={link.sms_text}
                onFocus={(e) => e.target.select()}
                data-testid="share-sms-textarea"
                className="flex-1 min-h-[92px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 font-mono resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="flex flex-col gap-1.5 shrink-0">
                <a
                  href={`sms:?&body=${encodeURIComponent(link.sms_text)}`}
                  data-testid="open-sms-app-btn"
                  className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-3 inline-flex items-center gap-1 text-sm font-medium justify-center"
                >
                  <Send className="w-3.5 h-3.5" /> Open in Messages
                </a>
                <Button
                  type="button"
                  onClick={() => copy(link.sms_text, "sms")}
                  data-testid="copy-sms-btn"
                  variant="outline"
                  className="rounded-lg h-8 px-3 inline-flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" /> {copiedField === "sms" ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              On your phone, tap <span className="font-semibold">Open in Messages</span> to jump straight into your texting app with the message pre-filled.
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Just the link</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={link.claim_url}
                onFocus={(e) => e.target.select()}
                data-testid="share-link-input"
                className="flex-1 rounded-lg border border-slate-200 bg-white px-3 h-10 text-xs text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <Button
                type="button"
                onClick={() => copy(link.claim_url, "url")}
                data-testid="copy-link-btn"
                variant="outline"
                className="rounded-lg h-10 px-3 shrink-0 inline-flex items-center gap-1"
              >
                <Copy className="w-3.5 h-3.5" /> {copiedField === "url" ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-slate-500">
            The link is single-lead — whoever clicks it first and signs up gets this lead free. Fires the same signup flow as emailed invites.
          </div>
        </div>
      )}
    </div>
  );
}


function AutoInviteButton({ lead, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm(`Auto-invite 5 nearby screen companies for this lead in ${lead.zip_code}?\n\nWe'll search Google Places, scrape emails from their websites, and send a curious-tone invite. Companies without emails get listed as "text manually" so you can SMS them yourself. 60-day dedupe and 6/year cap are enforced.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/auto-invite`, { count: 5 });
      setResult(data);
      const phoneCount = (data.phone_only_prospects || []).length;
      if (data.invited === 0 && phoneCount === 0) {
        toast.warning(data.message || "No prospects found — try a different ZIP or check the Places API key");
      } else if (data.invited === 0) {
        toast.info(`Emailed 0 · ${phoneCount} phone-only prospects ready to text`);
      } else {
        toast.success(`Auto-emailed ${data.invited} · ${phoneCount} more to text manually`);
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-invite failed");
    } finally {
      setBusy(false);
    }
  };

  const smsBodyFor = (p) => {
    // Preferred: use the shareable claim link generated by the backend during
    // auto-invite. This is the SAME wording the "Text it from your phone"
    // panel uses so the admin's outreach voice stays consistent across every
    // surface. We prefix with the business name so the recipient knows the
    // text is meant for them and not a broadcast blast.
    if (result?.share_link?.sms_text) {
      return `Hi ${p.business_name} — ${result.share_link.sms_text.replace(/^Hi\s*—\s*/, "")}`;
    }
    // Fallback if share link generation somehow failed — legacy "reply YES"
    // template so the Text button is never disabled.
    const zip = lead.zip_code || "your area";
    const amount = Math.round(Number(lead.estimate_total) || 0);
    return `Hi — ${p.business_name}? Quick one: a homeowner in ${zip} just quoted $${amount.toLocaleString()} through our platform (WindowScreenQuote). We can't route them to anyone yet — want them? Free, no card required. Reply YES and I'll send the claim link.`;
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="auto-invite-btn"
        title="Google Places discovery + email scrape + auto-send 5 outreach emails"
        className="rounded-xl h-11 px-4 bg-gradient-to-r from-[#0F172A] to-slate-800 hover:from-black hover:to-slate-900 text-white inline-flex items-center gap-1.5"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        Auto-invite 5 nearby
      </Button>
      {result && (
        <div data-testid="auto-invite-result" className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-700 max-w-lg space-y-3">
          <div className="font-semibold text-slate-900">
            Discovered {result.discovered} · Emailed {result.invited} · {(result.phone_only_prospects || []).length} to text
          </div>

          {result.sent?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">Emailed automatically</div>
              <ul className="space-y-1">
                {result.sent.map((s, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-[11px]">
                    <Sparkles className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span className="font-medium text-slate-900 truncate">{s.business_name}</span>
                    <span className="text-slate-500 truncate">· {s.email}</span>
                    {s.phone && <span className="text-slate-400 shrink-0">· {s.phone}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.phone_only_prospects?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#C75D33] mb-1.5">Text these manually · no email on file</div>
              <ul className="space-y-2">
                {result.phone_only_prospects.map((p) => (
                  <li key={p.prospect_id} data-testid={`phone-only-${p.prospect_id}`} className="rounded-md border border-slate-200 bg-white p-2 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-[12px] truncate flex items-center gap-1">
                        {p.business_name}
                        {p.rating && <span className="text-[10px] text-amber-600">★ {p.rating}{p.user_rating_count ? ` (${p.user_rating_count})` : ""}</span>}
                      </div>
                      <div className="text-[11px] text-slate-600 font-mono">{p.phone}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <a
                        href={`sms:${(p.phone || "").replace(/[^0-9+]/g, "")}?&body=${encodeURIComponent(smsBodyFor(p))}`}
                        data-testid={`phone-only-sms-${p.prospect_id}`}
                        className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                      >
                        <MessageCircle className="w-3 h-3" /> Text
                      </a>
                      <a
                        href={`tel:${(p.phone || "").replace(/[^0-9+]/g, "")}`}
                        className="rounded-md border border-slate-300 hover:bg-slate-100 text-slate-700 text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                      >
                        <Phone className="w-3 h-3" /> Call
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.skipped?.length > 0 && (
            <details className="text-slate-500">
              <summary className="text-[10px] font-bold uppercase tracking-widest cursor-pointer">Skipped ({result.skipped.length})</summary>
              <ul className="mt-1 space-y-0.5">
                {result.skipped.slice(0, 10).map((s, i) => (
                  <li key={i}>× {s.business_name}: {s.reason}{s.phone ? ` · ${s.phone}` : ""}</li>
                ))}
                {result.skipped.length > 10 && <li className="italic">…and {result.skipped.length - 10} more</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
